import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { EditValueDialog } from '@/ui';
import type { EditValueDialogHandle } from '@/ui';
import type { GeoPoint } from '@/signalr';
import {
  changeCallSign,
  changeDispatcherNote,
  changeIncidentLabel,
  changePatientDescription,
  updateCallerName,
} from '@ngsos/portal-core/commands';
import { subscribeIncidentDetailDialogRequests } from '../actions/dialogController';
import { AddPhoneNumberDialog } from './AddPhoneNumberDialog';
import type { AddPhoneNumberDialogHandle } from './AddPhoneNumberDialog';
import { AddAgencyToIncidentDialog } from './AddAgencyToIncidentDialog';
import type { AddAgencyToIncidentDialogHandle } from './AddAgencyToIncidentDialog';
import { ConfirmOrRejectIncidentShareDialog } from './ConfirmOrRejectIncidentShareDialog';
import type { ConfirmOrRejectIncidentShareDialogHandle } from './ConfirmOrRejectIncidentShareDialog';
import { HowToTurnOnGpsDialog } from './HowToTurnOnGpsDialog';
import type { HowToTurnOnGpsDialogHandle } from './HowToTurnOnGpsDialog';
import { ManualGpsCoordinatesDialog } from './ManualGpsCoordinatesDialog';
import type { ManualGpsCoordinatesDialogHandle } from './ManualGpsCoordinatesDialog';
import { MediaViewerDialog } from './MediaViewerDialog';
import type { MediaViewerDialogHandle } from './MediaViewerDialog';
import { ShareByLinkDialog } from './ShareByLinkDialog';
import type { ShareByLinkDialogHandle } from './ShareByLinkDialog';

export type IncidentDetailDialogsProps = {
  incidentId: string;
  lastCallerPosition: GeoPoint | null;
};

export function IncidentDetailDialogs({
  incidentId,
  lastCallerPosition,
}: IncidentDetailDialogsProps) {
  const { t } = useTranslation();

  const addPhoneNumberDialogRef = useRef<AddPhoneNumberDialogHandle>(null);
  const shareByLinkDialogRef = useRef<ShareByLinkDialogHandle>(null);
  const addAgencyToIncidentDialogRef = useRef<AddAgencyToIncidentDialogHandle>(null);
  const manualGpsCoordinatesDialogRef = useRef<ManualGpsCoordinatesDialogHandle>(null);
  const confirmOrRejectDialogRef = useRef<ConfirmOrRejectIncidentShareDialogHandle>(null);
  const howToTurnOnGpsDialogRef = useRef<HowToTurnOnGpsDialogHandle>(null);
  const mediaViewerDialogRef = useRef<MediaViewerDialogHandle>(null);

  const editCallerNameDialogRef = useRef<EditValueDialogHandle>(null);
  const editDispatcherNoteDialogRef = useRef<EditValueDialogHandle>(null);
  const editCallSignDialogRef = useRef<EditValueDialogHandle>(null);
  const editIncidentLabelDialogRef = useRef<EditValueDialogHandle>(null);
  const editPatientDescriptionDialogRef = useRef<EditValueDialogHandle>(null);

  const confirmOrRejectOnResultRef = useRef<(confirmed: boolean) => Promise<void>>(() => Promise.resolve());

  const incidentIdRef = useRef(incidentId);
  incidentIdRef.current = incidentId;

  useEffect(
    () =>
      subscribeIncidentDetailDialogRequests((request) => {
        if ('incidentId' in request && request.incidentId !== incidentIdRef.current) {
          console.warn('[incident-detail] Dialog request for a different incident dropped.', request);
          return;
        }

        switch (request.kind) {
          case 'phoneNumber':
            addPhoneNumberDialogRef.current?.showModal();
            break;
          case 'shareByLink':
            shareByLinkDialogRef.current?.showModal();
            break;
          case 'addAgencyToIncident':
            addAgencyToIncidentDialogRef.current?.showModal();
            break;
          case 'manualGpsCoordinates':
            manualGpsCoordinatesDialogRef.current?.showModal();
            break;
          case 'editCallerName':
            editCallerNameDialogRef.current?.showModal(request.currentValue ?? undefined);
            break;
          case 'editDispatcherNote':
            editDispatcherNoteDialogRef.current?.showModal(request.currentValue ?? undefined);
            break;
          case 'editCallSign':
            editCallSignDialogRef.current?.showModal(request.currentValue ?? undefined);
            break;
          case 'editIncidentLabel':
            editIncidentLabelDialogRef.current?.showModal(request.currentValue ?? undefined);
            break;
          case 'editPatientDescription':
            editPatientDescriptionDialogRef.current?.showModal(request.currentValue ?? undefined);
            break;
          case 'confirmOrRejectIncidentShare':
            confirmOrRejectOnResultRef.current = request.onResult;
            confirmOrRejectDialogRef.current?.show();
            break;
          case 'howToTurnOnGps':
            howToTurnOnGpsDialogRef.current?.show(request.deviceOs);
            break;
          case 'mediaViewer':
            mediaViewerDialogRef.current?.show(request.media, request.startIndex);
            break;
        }
      }),
    [],
  );

  return (
    <>
      <AddPhoneNumberDialog ref={addPhoneNumberDialogRef} incidentId={incidentId} />

      <ShareByLinkDialog ref={shareByLinkDialogRef} incidentId={incidentId} lastCallerPosition={lastCallerPosition} />

      <AddAgencyToIncidentDialog ref={addAgencyToIncidentDialogRef} incidentId={incidentId} />

      <ManualGpsCoordinatesDialog ref={manualGpsCoordinatesDialogRef} incidentId={incidentId} />

      <ConfirmOrRejectIncidentShareDialog
        ref={confirmOrRejectDialogRef}
        onResult={(confirmed) => confirmOrRejectOnResultRef.current(confirmed)}
      />

      <HowToTurnOnGpsDialog ref={howToTurnOnGpsDialogRef} />

      <MediaViewerDialog ref={mediaViewerDialogRef} />

      <EditValueDialog
        ref={editCallerNameDialogRef}
        label={t('Incident_InfoPanel_Name')}
        valueRequired
        onValueEdit={(value) => updateCallerName(incidentId, value)}
      />

      <EditValueDialog
        ref={editDispatcherNoteDialogRef}
        label={t('Incident_InfoPanel_DispatcherNote')}
        multiline
        onValueEdit={(value) => changeDispatcherNote(incidentId, value)}
      />

      <EditValueDialog
        ref={editCallSignDialogRef}
        label={t('Incident_InfoPanel_CallSign')}
        onValueEdit={(value) => changeCallSign(incidentId, value)}
      />

      <EditValueDialog
        ref={editIncidentLabelDialogRef}
        label={t('Incident_InfoPanel_IncidentLabel')}
        onValueEdit={(value) => changeIncidentLabel(incidentId, value)}
      />

      <EditValueDialog
        ref={editPatientDescriptionDialogRef}
        label={t('Incident_InfoPanel_Patient_Description')}
        multiline
        onValueEdit={(value) => changePatientDescription(incidentId, value)}
      />
    </>
  );
}
